import {
  contrastRatio,
  relativeLuminance,
  type KitFontId,
  type ReleaseThemeId,
  type ThemeTypeStyle,
} from '../releaseThemes';
import { textWidthEm } from './kitFontMetrics';
import { kitDateParts, kitWords, type KitTextLocale } from '../kitText';
import { releaseDayLabel, storyPillText } from './storyCopy';
import type { StoryColours } from './storyStyles';
import type { KitStory } from './storyTypes';
import { outBack, progress, round3 } from './videoMotion';

/**
 * The AI reel (design `asset-redesign/z6 AI reel - new`, owner answers 2026-10-08): one length of
 * 15 s that starts on the audio hook. Full-bleed bank clips with no colour wash, cut on the
 * downbeats of the theme grid; a 1 s hook card (artist and title in the display type); a release
 * chip top left for the whole reel; the synced lyric lines word by word on a scrim band; the cover in
 * its theme frame for 2 beats on the drop; "Link in bio · <SmartLink>" under the chip from the drop;
 * the end card in the Story order from about 12.2 s (cover, title one time, artist, the pill by the
 * date, the link line, the Free watermark). No waveform, no copy captions, no voiceover.
 *
 * Code makes every part of this file: the director only picks the clips. The server
 * (`buildReelTimeline`) and the web Studio preview read the same tables.
 */

/** The one length of an AI reel. */
export const AI_REEL_LENGTH_SEC = 15;

/**
 * How often a theme cuts (page z6): calm themes every 2 bars, Noir and Retro every bar, Hype and
 * Neon every bar and then every half bar after the drop.
 */
export type AiReelPace = 'calm' | 'bar' | 'fast';

export const AI_REEL_PACE: Readonly<Record<ReleaseThemeId, AiReelPace>> = {
  clean: 'calm',
  dreamy: 'calm',
  warm: 'calm',
  handmade: 'calm',
  dark: 'bar',
  retro: 'bar',
  hype: 'fast',
  neon: 'fast',
};

/** The pace of a theme id; an unknown id cuts as a calm theme. Pure. */
export function aiReelPaceOf(themeId: string): AiReelPace {
  return (AI_REEL_PACE as Record<string, AiReelPace | undefined>)[themeId] ?? 'calm';
}

/** The boxes of page z6, in px of the 1080 x 1920 frame. */
export const AI_REEL_LAYOUT = {
  /** The release chip: x 148, y 296, 72 px high, the text 32 px times the text factor, a 14 px dot. */
  chip: { x: 148, y: 296, height: 72, paddingX: 30, textPx: 32, dotPx: 14 },
  /** "Link in bio · <SmartLink>" under the chip, 28 px times the text factor. */
  link: { x: 148, y: 384, textPx: 28 },
  /** The hook card: bottom-aligned in x 140 to 940, y 760 to 1180; the artist over the title. */
  hook: { x: 140, top: 760, bottom: 1180, w: 800, artistPx: 48, titlePx: 180, titleMaxW: 780, gap: 12 },
  /** The lyric column: the sung line ends at y 1180, the next line starts at y 1240; it lives in y 760 to 1440. */
  column: { x: 140, top: 760, bottom: 1440, w: 800, sungBottom: 1180, gap: 60 },
  /** The cover in its theme frame on the drop: 440 px, x 320 to 760, y 450 to 890. */
  cover: { x: 320, y: 450, size: 440 },
  /** The scrim of the dark theme colour behind the text band: alpha by y. */
  scrim: [
    { y: 600, alpha: 0 },
    { y: 890, alpha: 0.52 },
    { y: 1370, alpha: 0.62 },
    { y: 1700, alpha: 0 },
  ],
} as const;

/**
 * The end card stack starts at y 380 (page z6: the cover under the chip and the link line) and ends
 * by y 1440 (the video safe column).
 */
export const AI_REEL_END_CARD = { top: 380, bottom: 1440 } as const;

/** The end card items that stay at the foot of the stack: the pill, the link line and the watermark. */
export const AI_REEL_END_CARD_FOOT_KINDS: readonly string[] = ['pill', 'link', 'watermark'];

/** The least room between the artist and the pill when the head of the end card moves down. */
const END_CARD_HEAD_GAP = 60;

/**
 * How far the head of the end card (the cover, the title and the artist of the `video916` Story
 * layout, which starts at y 270) moves down so that it starts at y 380 under the chip and the link
 * line (page z6). The foot (the pill and the link line) stays; the head never comes closer to the
 * pill than 60 px. Pure.
 */
export function aiReelEndCardShift(items: ReadonlyArray<{ kind: string; box: { y: number; h: number } }>): number {
  const head = items.filter((item) => !AI_REEL_END_CARD_FOOT_KINDS.includes(item.kind));
  const foot = items.filter((item) => AI_REEL_END_CARD_FOOT_KINDS.includes(item.kind));
  if (head.length === 0) {
    return 0;
  }
  const top = Math.min(...head.map((item) => item.box.y));
  const bottom = Math.max(...head.map((item) => item.box.y + item.box.h));
  const limit =
    foot.length === 0 ? AI_REEL_END_CARD.bottom : Math.min(...foot.map((item) => item.box.y)) - END_CARD_HEAD_GAP;
  const wanted = Math.max(0, AI_REEL_END_CARD.top - top);
  return Math.round(Math.max(0, Math.min(wanted, limit - bottom)));
}

/** The zoom of a clip on a downbeat inside a shot, and how long it lasts. */
export const AI_REEL_PUNCH = { scale: 1.03, sec: 0.2 } as const;
/** The cover moment lasts 2 beats from the drop. */
export const AI_REEL_COVER_BEATS = 2;
/** The poster: 0.5 s, the hook card on the first clip. */
export const AI_REEL_POSTER_SEC = 0.5;

/**
 * The texts and the timing of one AI reel (`timeline.kitShort`, kind `aiReel`). The times are reel
 * seconds. The footage is the segments of the timeline; the lyric lines are its lyric layers.
 */
export interface KitAiReelVideo {
  kind: 'aiReel';
  title: string;
  artist: string;
  /** The hook card shows from 0 to this time (the first beat after 1 s). */
  hookEndSec: number;
  /** The release chip ("Out Friday", "Out Fri 16 Oct", "Out now"). Null: no release date, no chip. */
  chip: string | null;
  /** "Link in bio · <SmartLink>" from the drop to the end card. Null: the song has no published SmartLink. */
  linkLine: string | null;
  /** The drop: the downbeat with the biggest rise of bar energy. */
  dropSec: number;
  /** The cover moment: from the drop for 2 beats. Null: no room for it. */
  coverMoment: { fromSec: number; toSec: number } | null;
  /** The end card starts on this downbeat (about 12.2 s). */
  endCardSec: number;
  /** The end card: the Story stack in the video format (cover, title, artist, pill, link line, watermark). */
  end: KitStory;
  /** The downbeats of the window: the clip punches on the ones inside a shot, the end card cover kicks on each. */
  downbeats: number[];
  /** The cut times (reel seconds, the first is 0): a punch never lands on a cut. */
  cuts: number[];
  /** The poster frame time. */
  posterSec: number;
}

/**
 * The release chip (page z6, the banner rule of the UTC day): "Out now" from the release day on,
 * "Out Friday" 1 to 6 days before, "Out Fri 16 Oct" 7 days or more before. Null with no release date
 * (or a date that the code cannot read). Pure.
 */
export function aiReelChipText(
  releaseDate: string | null,
  today: string,
  locale?: KitTextLocale | null
): string | null {
  const words = kitWords(locale);
  if (releaseDate === null) {
    return null;
  }
  const parse = (value: string): number | null => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    return match === null ? null : Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  };
  const release = parse(releaseDate);
  const now = parse(today);
  if (release === null || now === null) {
    return null;
  }
  const days = Math.round((release - now) / 86_400_000);
  if (days <= 0) {
    return words.outNow;
  }
  if (days < 7) {
    const parts = kitDateParts(releaseDate);
    return parts === null ? null : (words.outOnWeekday[parts.weekday] ?? null);
  }
  const label = releaseDayLabel(releaseDate, locale);
  return label === null ? null : words.outDate(label);
}

/** "Link in bio · laziibee.music24.link/swamps"; null with no published SmartLink. Pure. */
export function aiReelLinkLine(link: string | null, locale?: KitTextLocale | null): string | null {
  return link === null ? null : `${kitWords(locale).linkInBio} · ${link}`;
}

/**
 * The end card of an AI reel: the Story stack in the 9:16 video format (`video916`) with no hero and
 * no kicker: the cover, the title one time, the artist, the pill by the date ("Pre-save now",
 * "Listen now", "Coming soon" with no published pre-save page) and "Link in bio · <SmartLink>".
 * Pure.
 */
export function buildAiReelEndCard(input: {
  title: string;
  artist: string;
  releaseDate: string | null;
  today: string;
  link: string | null;
  presave?: boolean;
  /** The language of the words; absent: English. */
  locale?: KitTextLocale | null;
}): KitStory {
  return {
    format: 'video916',
    moment: 'preSave',
    title: input.title,
    artist: input.artist,
    kicker: null,
    hero: null,
    nowWord: null,
    dayLabel: null,
    when: null,
    cta: storyPillText('lyric', input.releaseDate, input.today, input.presave, input.locale),
    link: input.link,
    linkNote: input.link === null ? null : kitWords(input.locale).linkInBio,
    withLogo: false,
  };
}

/** The hook title never gets smaller than this. */
export const AI_REEL_HOOK_MIN_TITLE_PX = 80;

/**
 * The size of the hook card title: 180 px times the display factor of the theme, smaller in steps of
 * 6 px (not under 80 px) until the title is at most 780 px wide. Pure.
 */
export function aiReelHookTitlePx(input: {
  title: string;
  font: KitFontId;
  style: Pick<ThemeTypeStyle, 'case' | 'tracking'>;
  displayFactor: number;
}): number {
  const { hook } = AI_REEL_LAYOUT;
  const widthEm = textWidthEm(input.title, input.font, input.style);
  let size = Math.round(hook.titlePx * input.displayFactor);
  while (size > AI_REEL_HOOK_MIN_TITLE_PX && widthEm * size > hook.titleMaxW) {
    size = Math.max(AI_REEL_HOOK_MIN_TITLE_PX, size - 6);
  }
  return size;
}

export interface AiReelPose {
  /** The hook card: 1 while it shows, 0 after it. */
  hook: number;
  /** The clip zoom: 1.03 for 0.2 s on a downbeat inside a shot, else 1. */
  punch: number;
  /** True while the cover moment shows. */
  cover: boolean;
  /** The link line shows (from the drop to the end card). */
  link: boolean;
  /** True from the end card on. */
  ended: boolean;
  /** The end card cover: 1.05 on each downbeat, back in 0.22 s. */
  coverKick: number;
  /** The end card pill: 1.12 with a spring on the last downbeat. */
  pillScale: number;
}

/** The pose of the parts of an AI reel at the reel time `t`. Pure. */
export function aiReelPose(
  t: number,
  video: Pick<KitAiReelVideo, 'hookEndSec' | 'dropSec' | 'coverMoment' | 'endCardSec' | 'downbeats' | 'cuts'>
): AiReelPose {
  const ended = t >= video.endCardSec;
  const lastBeat = [...video.downbeats].reverse().find((beat) => beat <= t);
  const since = lastBeat === undefined ? Number.POSITIVE_INFINITY : t - lastBeat;
  const onCut = lastBeat !== undefined && video.cuts.some((cut) => Math.abs(cut - lastBeat) < 0.05);
  const punch = !ended && !onCut && since < AI_REEL_PUNCH.sec ? AI_REEL_PUNCH.scale : 1;
  const kick = ended && since < 0.22 ? 1 - since / 0.22 : 0;
  const lastDownbeat = video.downbeats.filter((beat) => beat >= video.endCardSec).at(-1) ?? video.endCardSec;
  const spring = lastDownbeat > video.endCardSec ? outBack(progress(t, lastDownbeat, lastDownbeat + 0.36)) : 0;
  return {
    hook: t < video.hookEndSec ? 1 : 0,
    punch,
    cover: video.coverMoment !== null && t >= video.coverMoment.fromSec && t < video.coverMoment.toSec,
    link: !ended && t >= video.dropSec,
    ended,
    coverKick: round3(1 + 0.05 * kick * kick),
    pillScale: round3(1 + 0.12 * spring),
  };
}

/**
 * The colours of the text on the footage (page z6): the dark colour of the theme for the scrim, the
 * light colour for the text (the lighter of the ground and the text colour; the lightest colour when
 * that one is still dark), and the colour of the sung word (the accent when it has 3:1 on the scrim,
 * else the light colour). Pure.
 */
export function aiReelFootageColours(colors: Pick<StoryColours, 'ink' | 'surface' | 'text' | 'accent'>): {
  dark: string;
  light: string;
  nowWord: string;
} {
  const all = [colors.ink, colors.surface, colors.text, colors.accent];
  const dark = all.reduce((low, colour) => (relativeLuminance(colour) < relativeLuminance(low) ? colour : low));
  let light = relativeLuminance(colors.ink) > relativeLuminance(colors.text) ? colors.ink : colors.text;
  if (relativeLuminance(light) < 0.35) {
    light = all.reduce((high, colour) => (relativeLuminance(colour) > relativeLuminance(high) ? colour : high));
  }
  const nowWord = colors.accent !== light && contrastRatio(colors.accent, dark) >= 3 ? colors.accent : light;
  return { dark, light, nowWord };
}

/**
 * The Free watermark of an AI reel (owner rule 2026-10-08: on every frame of a Free file; design w0):
 * the stack lockup, glass, centred x 540 with its foot at y 1440 for all 15 s (`aiReelWatermark` of
 * `watermark.ts`). On Free the lyric column ends at y 1290, and the end card stack reserves the
 * lockup room at its foot, so the lockup never moves and nothing runs under it.
 */
export const AI_REEL_FREE_COLUMN_BOTTOM = 1290;

/** The foot of the lyric column: y 1440, or y 1290 over the Free lockup. Pure. */
export function aiReelColumnBottom(watermark: boolean): number {
  return watermark ? AI_REEL_FREE_COLUMN_BOTTOM : AI_REEL_LAYOUT.column.bottom;
}
